import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ORG_TYPES = [
  { v: "cse", l: "CSE / Comité social et économique" },
  { v: "entreprise", l: "Entreprise" },
  { v: "association", l: "Association" },
  { v: "club", l: "Club sportif ou culturel" },
  { v: "amicale", l: "Amicale" },
  { v: "administration", l: "Administration" },
  { v: "collectivite", l: "Collectivité" },
  { v: "autre", l: "Autre organisme" },
];

const EMPTY = {
  org_type: "cse",
  name: "",
  responsible_name: "",
  responsible_role: "",
  email: "",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
  website: "",
  members_count: 0,
  email_domain: "",
  auto_attach_by_domain: false,
  description: "",
  admin_comment: "",
};

export default function AdminOrgCreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.responsible_name || !form.email) {
      toast.error("Nom, responsable et email sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, members_count: Number(form.members_count) || 0 };
      const { data } = await api.post("/admin/organizations", payload);
      toast.success(`Organisme créé — identifiants envoyés à ${data.organization.email}`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.(data.organization);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="btn-shine text-white" data-testid="admin-org-create-open">
        <Plus size={14} className="mr-1"/> Créer un organisme
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-org-create-dialog">
          <DialogHeader>
            <DialogTitle>Créer un nouvel organisme</DialogTitle>
            <DialogDescription>
              L'organisme sera créé en statut <b>actif</b>. Un mot de passe temporaire sera généré et envoyé par email au responsable.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Type d'organisme *</Label>
                <Select value={form.org_type} onValueChange={(v) => set("org_type", v)}>
                  <SelectTrigger data-testid="admin-org-create-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nom de l'organisme *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex: CSE Entreprise XYZ"
                       data-testid="admin-org-create-name" required/>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Nom du responsable *</Label>
                <Input value={form.responsible_name} onChange={(e) => set("responsible_name", e.target.value)}
                       data-testid="admin-org-create-responsible" required/>
              </div>
              <div>
                <Label>Fonction</Label>
                <Input value={form.responsible_role} onChange={(e) => set("responsible_role", e.target.value)}
                       placeholder="Ex: Secrétaire du CSE"
                       data-testid="admin-org-create-role"/>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Email du responsable *</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                       data-testid="admin-org-create-email" required/>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                       data-testid="admin-org-create-phone"/>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label>Adresse</Label>
                <Input value={form.address} onChange={(e) => set("address", e.target.value)}
                       data-testid="admin-org-create-address"/>
              </div>
              <div>
                <Label>Code postal</Label>
                <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)}
                       data-testid="admin-org-create-postal"/>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Ville</Label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)}
                       data-testid="admin-org-create-city"/>
              </div>
              <div>
                <Label>Site web</Label>
                <Input value={form.website} onChange={(e) => set("website", e.target.value)}
                       placeholder="https://..."
                       data-testid="admin-org-create-website"/>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Nombre de membres estimé</Label>
                <Input type="number" min="0" value={form.members_count}
                       onChange={(e) => set("members_count", e.target.value)}
                       data-testid="admin-org-create-members"/>
              </div>
              <div>
                <Label>Domaine email des membres</Label>
                <Input value={form.email_domain} onChange={(e) => set("email_domain", e.target.value)}
                       placeholder="entreprise.fr"
                       data-testid="admin-org-create-domain"/>
              </div>
            </div>

            {form.email_domain && (
              <label className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <Checkbox checked={form.auto_attach_by_domain}
                          onCheckedChange={(v) => set("auto_attach_by_domain", !!v)}
                          data-testid="admin-org-create-autoattach"/>
                <span>Auto-rattachement des membres par domaine email <b>@{form.email_domain}</b></span>
              </label>
            )}

            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
                        placeholder="Présentation courte de l'organisme..."
                        data-testid="admin-org-create-description"/>
            </div>

            <div>
              <Label>Note admin (interne)</Label>
              <Textarea rows={2} value={form.admin_comment} onChange={(e) => set("admin_comment", e.target.value)}
                        placeholder="Note interne non visible par l'organisme..."
                        data-testid="admin-org-create-note"/>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}
                      data-testid="admin-org-create-cancel">Annuler</Button>
              <Button type="submit" disabled={saving} className="btn-shine text-white"
                      data-testid="admin-org-create-submit">
                {saving ? <><Loader2 size={14} className="mr-1 animate-spin"/> Création...</> : "Créer l'organisme"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
